import { useInfiniteQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useDebounce } from './useDebounce'
import projectService from '../services/projectService'

/**
 * Custom hook for fetching projects with infinite scroll
 * @param {object} filters - Filter options
 * @returns {object} Query result with projects and pagination
 */
export const useInfiniteProjects = (filters = {}) => {
  const { category = 'all', search = '', limit = 20 } = filters
  
  // Debounce search to avoid excessive API calls
  const debouncedSearch = useDebounce(search, 500)
  
  const queryKey = ['projects', 'infinite', category, debouncedSearch, limit]
  
  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    status,
    refetch,
    isLoading,
    isError
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = null }) => {
      const result = await projectService.getPublicProjects({
        category: category === 'all' ? undefined : category,
        search: debouncedSearch || undefined,
        limit,
        cursor: pageParam
      })
      
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch projects')
      }
      
      return result
    },
    getNextPageParam: (lastPage) => {
      return lastPage.pagination?.hasMore ? lastPage.pagination.nextCursor : undefined
    },
    enabled: true,
    staleTime: 0, // Always fetch fresh data
    cacheTime: 5 * 60 * 1000, // 5 minutes cache time
  })

  // Flatten all pages into a single array of projects
  const projects = useMemo(() => {
    if (!data?.pages) return []
    
    return data.pages.reduce((acc, page) => {
      return [...acc, ...(page.data || [])]
    }, [])
  }, [data])

  // Calculate total count (approximate)
  const totalCount = useMemo(() => {
    return projects.length
  }, [projects.length])

  return {
    projects,
    totalCount,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    isError,
    refetch,
    status
  }
}

export default useInfiniteProjects
